import { defineStore } from "pinia";
import App from "@bloglovin/inspire-engine/App";
import {
  initialState, step, countTextWords, projectTabFromPath, toRecordParams,
} from "+/Q3po/Components/auditPresenceMachine.mjs";

const COMPOSER_SELECTOR = ".project-chat-compose";
const NAV_KEYS = new Set(["PageUp", "PageDown", "ArrowUp", "ArrowDown", "Home", "End", " "]);
const TICK_MS = 1000;

const isEditable = target => !!(target && target.closest
  && target.closest("input, textarea, select, [contenteditable=''], [contenteditable='true']"));
function composerOf(target) {
  return target && target.closest ? target.closest(COMPOSER_SELECTOR) : null;
}

export const useProjectAuditPresenceStore = defineStore("project-audit-presence-store", {
  state: () => ({
    started: false,
    projectId: 0,
    machine: initialState(""),
  }),
  actions: {
    start() {
      if (this.started || typeof window === "undefined") {
        return;
      }
      this.started = true;
      const now = () => Date.now();
      const intent = () => this.feed({ type: "intent", at: now() });
      window.addEventListener("wheel", intent, { capture: true, passive: true });
      window.addEventListener("touchmove", intent, { capture: true, passive: true });
      window.addEventListener("pointerdown", event => {
        if (!composerOf(event.target)) {
          intent();
        }
      }, { capture: true, passive: true });
      window.addEventListener("keydown", event => {
        const composer = composerOf(event.target);
        if (composer) {
          if (event.key === "Enter" && !event.shiftKey) {
            this.feed({ type: "key", at: now() });
            this.feed({ type: "send", words: countTextWords(composer.innerText), at: now() });
            return;
          }
          this.feed({ type: "key", at: now() });
          return;
        }
        if (NAV_KEYS.has(event.key) && !isEditable(event.target)) {
          intent();
        }
      }, { capture: true });
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") {
          this.feed({ type: "hidden", at: now() });
        }
      });
      window.addEventListener("pagehide", () => this.feed({ type: "leave", at: now() }));
      window.addEventListener("inspire:navigate", () => this.syncLocation());
      window.addEventListener("popstate", () => this.syncLocation());
      window.setInterval(() => this.feed({ type: "tick", at: now() }), TICK_MS);
      this.syncLocation();
    },
    syncLocation() {
      const location = projectTabFromPath(window.location.pathname);
      const at = Date.now();
      if (!location || location.projectId !== this.projectId) {
        this.feed({ type: "leave", at });
        this.projectId = location ? location.projectId : 0;
        this.machine = initialState(location ? location.tab : "");
        return;
      }
      if (location.tab !== this.machine.tab) {
        this.feed({ type: "tab", tab: location.tab, at });
      }
    },
    feed(event) {
      if (!this.projectId) {
        return;
      }
      const { state, closed } = step(this.machine, event);
      this.machine = state;
      for (const session of closed) {
        this.record(session);
      }
    },
    record(session) {
      const projectId = this.projectId;
      try {
        App.get("ProjectManagement").triggerEvent("RecordMyAuditSession", toRecordParams(session, projectId));
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(
          `audit presence: RecordMyAuditSession failed for project ${projectId} `
          + `(${session.activity} on ${session.tab}, ${session.durationMs}ms, ${session.wordCount} words) — session discarded`,
          e,
        );
      }
    },
  },
});
